import { useState, isValidElement, useMemo, useRef, useEffect } from "react";
import { Form, useFormikContext, Field } from "formik";
import { Radio } from "app/components/form/Radio";
import CheckboxFormik from "app/pages/.shared/form/CheckboxFormik";
import PropTypes from "prop-types";
import { useFacets } from "../../hooks/useFacets";
import Button, { BUTTON_VARIANTS, BUTTON_SIZES } from "app/pages/.shared/form/Button";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import { FormattedMessage, useIntl } from "react-intl";
import { FILTER_SELECTION_TYPES } from "../../constants/filterSelectionTypes";
import Searchbar from "app/pages/HotelOnly/components/Searchbar/Searchbar";
import "./FilterPopoverForm.scss";
import { useIntersectionObserver } from "app/hooks";
import classNames from "classnames";
import { sendTagOnProductsFilter } from "app/utils/analytics";

const isJSX = value => isValidElement(value);

const OPTIONS_CHUNK_SIZE = 30;

export const FilterPopoverForm = ({ facetId }) => {
	const { formatMessage } = useIntl();
	const { values, setFieldValue, resetForm } = useFormikContext();
	const { facets } = useFacets();

	const [query, setQuery] = useState("");
	const [visibleCount, setVisibleCount] = useState(OPTIONS_CHUNK_SIZE);
	const listRef = useRef(null);

	const selectedValuesCount = values?.[facetId]?.length || 0;

	const facet = facets[facetId];
	const { selectionMode, options, isSearchable, search, title } = facet;

	const filteredOptions = useMemo(() => {
		return query.length > 0
			? options.filter(option =>
					option.label
						.toString()
						.toLowerCase()
						.includes(query.toLowerCase())
			  )
			: options;
	}, [query, options]);

	const visibleOptions = useMemo(() => filteredOptions.slice(0, visibleCount), [
		filteredOptions,
		visibleCount,
	]);

	const [sentinelRef, isVisible] = useIntersectionObserver({
		root: listRef,
		rootMargin: "0px 0px 150px 0px",
		disabled: visibleCount >= filteredOptions.length,
	});

	useEffect(() => {
		setVisibleCount(OPTIONS_CHUNK_SIZE);
	}, [filteredOptions]);

	useEffect(() => {
		if (!isVisible || visibleCount >= filteredOptions.length) {
			return;
		}

		setVisibleCount(prev => Math.min(prev + OPTIONS_CHUNK_SIZE, filteredOptions.length));
	}, [isVisible, filteredOptions.length]);

	const handleQueryChange = e => {
		setQuery(e.target.value);
	};

	const handleResetFacet = () => resetForm({ values: { ...values, [facetId]: [] } });

	const handleChange = value => {
		const newValue = Array.isArray(value) ? value : [value];
		setFieldValue(facetId, newValue);
	};

	const handleApply = () => {
		sendTagOnProductsFilter(facetId, values?.[facetId] || null);
	};

	const canShowResetButton = selectedValuesCount > 0;

	return (
		<Form className="filter-popover-form">
			{isSearchable && (
				<header className="filter-popover-form__search">
					<Searchbar
						className="filter-drawer-form-panel__searchbar"
						value={query}
						onChange={handleQueryChange}
						placeholder={formatMessage({
							id: search?.placeholderKey || "sdp.search.cta.label",
						})}
					/>
				</header>
			)}

			<fieldset className="filter-popover-form__options">
				<legend className="sr-only">{title}</legend>

				<ul className="filter-popover-form__options-list" ref={listRef}>
					{visibleOptions.length === 0 && (
						<li className="filter-popover-form__no-results">
							<Typography variant={TYPOGRAPHY_VARIANTS.REGULAR} component="p">
								<FormattedMessage id="faq.search.no.result" />
							</Typography>
						</li>
					)}

					{visibleOptions.map(option => {
						const { value, label, labelPrefix, count } = option;

						return (
							<li key={option.value} className="filter-popover-form__option">
								{selectionMode === FILTER_SELECTION_TYPES.SINGLE ? (
									<Radio
										key={String(value)}
										name={facetId}
										value={String(value)}
										className="filter-popover-form__radio"
										disabled={count === 0}
										onChange={() => {
											handleChange(String(value));
										}}
									>
										<div className="filter-popover-form__radio-content">
											{isJSX(labelPrefix) && labelPrefix}

											<Typography
												variant={TYPOGRAPHY_VARIANTS.REGULAR}
												component="span"
											>
												{label}
											</Typography>

											<Typography
												variant={TYPOGRAPHY_VARIANTS.REGULAR}
												component="span"
												className="filter-popover-form__count-text"
											>
												{count}
											</Typography>
										</div>
									</Radio>
								) : (
									<Field
										key={String(value)}
										id={String(value)}
										name={facetId}
										component={CheckboxFormik}
										size={16}
										value={String(value)}
										grouped={true}
										className="filter-popover-form__checkbox"
										onUpdate={value => {
											handleChange(value);
										}}
										disabled={count === 0}
									>
										{isJSX(labelPrefix) && labelPrefix}

										<Typography
											variant={TYPOGRAPHY_VARIANTS.REGULAR}
											component="span"
										>
											{label}
										</Typography>

										<Typography
											variant={TYPOGRAPHY_VARIANTS.REGULAR}
											component="span"
											className="filter-popover-form__count-text"
										>
											{count}
										</Typography>
									</Field>
								)}
							</li>
						);
					})}

					{visibleCount < filteredOptions.length && (
						<li ref={sentinelRef} aria-hidden="true" />
					)}
				</ul>
			</fieldset>

			<footer className="filter-popover-form__actions">
				<Button
					size={BUTTON_SIZES.SMALL}
					className="filter-popover-form__apply-button"
					onClick={handleApply}
					type="submit"
					label={formatMessage({
						id: "sdp.filter.apply.button.label",
					})}
				/>

				<Button
					variant={BUTTON_VARIANTS.BORDERLESS}
					size={BUTTON_SIZES.SMALL}
					onClick={handleResetFacet}
					className={classNames("filter-popover-form__reset-button", {
						"filter-popover-form__reset-button--visible": canShowResetButton,
					})}
					aria-hidden={!canShowResetButton}
					tabIndex={canShowResetButton ? 0 : -1}
					label={formatMessage({
						id: "sdp.filter.reset.button.label",
					})}
				/>
			</footer>
		</Form>
	);
};

FilterPopoverForm.propTypes = {
	facetId: PropTypes.string.isRequired,
};
